import React, { useState, useMemo } from 'react';
import { 
  Ticket, Gift, Tag, Percent, DollarSign, Award, Sparkles, Plus, 
  Trash2, Edit2, CheckCircle, Copy, Share2, AlertCircle, RefreshCw, 
  Eye, Calendar, ArrowRight, ShieldCheck, Zap, Check, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function LoyaltyCoupons({ activeBusiness, onUpdateBusiness }) {
  const [coupons, setCoupons] = useState(() => {
    const defaultCoupons = [
      {
        id: 'cp_1',
        codigo: 'KEYLA20',
        tipo: 'percentage',
        valor: 20,
        limite_usos: 50,
        usos_actuales: 14,
        minimo_compra: 0,
        estado: 'active',
        fecha_fin: '2026-12-31',
        descripcion: '20% OFF de bienvenida para nuevos clientes en su primera reserva.'
      },
      {
        id: 'cp_2',
        codigo: 'VERANO5000',
        tipo: 'fixed',
        valor: 5000,
        limite_usos: 100,
        usos_actuales: 42,
        minimo_compra: 25000,
        estado: 'active',
        fecha_fin: '2026-10-31',
        descripcion: '$5,000 de descuento en servicios o consumos superiores a $25,000.'
      },
      {
        id: 'cp_3',
        codigo: 'VIPREWARD',
        tipo: 'percentage',
        valor: 15,
        limite_usos: 999,
        usos_actuales: 88,
        minimo_compra: 10000,
        estado: 'active',
        fecha_fin: '2026-11-30',
        descripcion: '15% de descuento especial recurrente para clientes del club VIP.'
      }
    ];

    if (Array.isArray(activeBusiness?.coupons) && activeBusiness.coupons.length > 0) {
      return activeBusiness.coupons;
    }
    return defaultCoupons;
  });

  // Estado para el programa de sellos/puntos (Loyalty Card)
  const [loyaltyConfig, setLoyaltyConfig] = useState(() => ({
    enabled: activeBusiness?.loyalty_config?.enabled ?? true,
    visitsRequired: activeBusiness?.loyalty_config?.visitsRequired || 5,
    rewardType: activeBusiness?.loyalty_config?.rewardType || 'free_service', // 'free_service', 'discount_50'
    rewardLabel: activeBusiness?.loyalty_config?.rewardLabel || '1 Servicio Gratis o 50% en Tratamiento VIP'
  }));

  // Modal para crear o editar cupón
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const [formCodigo, setFormCodigo] = useState('');
  const [formTipo, setFormTipo] = useState('percentage'); // 'percentage' | 'fixed'
  const [formValor, setFormValor] = useState(10);
  const [formLimite, setFormLimite] = useState(100);
  const [formMinimo, setFormMinimo] = useState(0);
  const [formFechaFin, setFormFechaFin] = useState('2026-12-31');
  const [formDescripcion, setFormDescripcion] = useState('');

  // Sincronización con maestro activeBusiness
  const saveToBusiness = (updatedCoupons, updatedLoyalty) => {
    setCoupons(updatedCoupons);
    if (onUpdateBusiness && activeBusiness) {
      onUpdateBusiness({
        ...activeBusiness,
        coupons: updatedCoupons,
        loyalty_config: updatedLoyalty || loyaltyConfig
      });
    }
  };

  // Generar código promo aleatorio
  const handleGenerateRandomCode = () => {
    const prefixes = ['PROMO', 'KEYLA', 'VIP', 'OFERTA', 'FLASH', 'REGALO'];
    const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomNum = Math.floor(10 + Math.random() * 90);
    setFormCodigo(`${randomPrefix}${randomNum}`);
    toast.success("Código generado automáticamente");
  };

  // Abrir modal creación
  const handleOpenNew = () => {
    setEditingCoupon(null);
    setFormCodigo('');
    setFormTipo('percentage');
    setFormValor(15);
    setFormLimite(50);
    setFormMinimo(0);
    setFormFechaFin('2026-12-31');
    setFormDescripcion('');
    setIsModalOpen(true);
  };

  // Abrir modal edición
  const handleOpenEdit = (cp) => {
    setEditingCoupon(cp);
    setFormCodigo(cp.codigo);
    setFormTipo(cp.tipo);
    setFormValor(cp.valor);
    setFormLimite(cp.limite_usos);
    setFormMinimo(cp.minimo_compra || 0);
    setFormFechaFin(cp.fecha_fin || '2026-12-31');
    setFormDescripcion(cp.descripcion || '');
    setIsModalOpen(true);
  };

  // Guardar Cupón
  const handleSaveCoupon = (e) => {
    e.preventDefault();
    if (!formCodigo.trim()) {
      toast.error("El código del cupón es obligatorio");
      return;
    }

    const newCouponObj = {
      id: editingCoupon ? editingCoupon.id : `cp_${Date.now()}`,
      codigo: formCodigo.toUpperCase().trim(),
      tipo: formTipo,
      valor: Number(formValor),
      limite_usos: Number(formLimite),
      usos_actuales: editingCoupon ? editingCoupon.usos_actuales : 0,
      minimo_compra: Number(formMinimo),
      estado: editingCoupon ? editingCoupon.estado : 'active',
      fecha_fin: formFechaFin,
      descripcion: formDescripcion.trim() || `${formTipo === 'percentage' ? `${formValor}% OFF` : `$${formValor} de descuento`}`
    };

    let updated;
    if (editingCoupon) {
      updated = coupons.map(c => c.id === editingCoupon.id ? newCouponObj : c);
      toast.success(`Cupón ${newCouponObj.codigo} actualizado correctamente`);
    } else {
      updated = [newCouponObj, ...coupons];
      toast.success(`Cupón ${newCouponObj.codigo} creado y activo`);
    }

    saveToBusiness(updated);
    setIsModalOpen(false);
  };

  // Cambiar estado activo/pausado
  const handleToggleStatus = (id) => {
    const updated = coupons.map(c => {
      if (c.id === id) {
        const nextState = c.estado === 'active' ? 'paused' : 'active';
        toast.success(`Cupón ${c.codigo} marcado como ${nextState === 'active' ? 'ACTIVO' : 'PAUSADO'}`);
        return { ...c, estado: nextState };
      }
      return c;
    });
    saveToBusiness(updated);
  };

  // Eliminar cupón
  const handleDeleteCoupon = (id, codigo) => {
    const updated = coupons.filter(c => c.id !== id);
    saveToBusiness(updated);
    toast.success(`Cupón ${codigo} eliminado`);
  };

  // Compartir por WhatsApp
  const handleShareWhatsApp = (cp) => {
    const bizName = activeBusiness?.nombre_negocio || 'Keyla CRM';
    const descPromo = cp.tipo === 'percentage' ? `${cp.valor}% OFF` : `$${cp.valor} de descuento`;
    const bookUrl = `https://keyla.seryiads.com/app/${activeBusiness?.id || 'reserva'}`;
    const text = `¡Hola! 🎁 Te regalamos el código promocional *${cp.codigo}* con *${descPromo}* para tu próxima cita o pedido en *${bizName}*. Canjéalo ahora en nuestro portal en línea: ${bookUrl}`;
    
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
    toast.success("Abriendo WhatsApp con tu promoción...");
  };

  // Guardar configuración del programa de sellos (Loyalty)
  const handleToggleLoyaltyProgram = () => {
    const updatedLoyalty = { ...loyaltyConfig, enabled: !loyaltyConfig.enabled };
    setLoyaltyConfig(updatedLoyalty);
    saveToBusiness(coupons, updatedLoyalty);
    toast.success(`Programa de Lealtad ${updatedLoyalty.enabled ? 'ACTIVADO ✨' : 'DESACTIVADO'}`);
  };

  // Estadísticas del módulo
  const stats = useMemo(() => {
    const activos = coupons.filter(c => c.estado === 'active').length;
    const totalCanjes = coupons.reduce((acc, c) => acc + (c.usos_actuales || 0), 0);
    const ahorroGenerado = coupons.reduce((acc, c) => {
      const perUse = c.tipo === 'fixed' ? c.valor : (25000 * c.valor / 100);
      return acc + ((c.usos_actuales || 0) * perUse);
    }, 0);
    return { activos, totalCanjes, ahorroGenerado };
  }, [coupons]);

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* Cabecera Principal del Módulo */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-seryi-bg border border-seryi-bg-light p-6 md:p-8 rounded-3xl shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-2">
            <Gift size={15} className="animate-bounce" />
            <span>Marketing de Recompensa & Retención</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-seryi-heading tracking-tight">
            Fidelización VIP y Cupones de Descuento
          </h2>
          <p className="text-sm text-seryi-text mt-1 max-w-xl">
            Crea códigos promocionales para tu portal online y activa la tarjeta digital de sellos VIP para premiar la preferencia de tus clientes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 relative z-10">
          <button
            onClick={handleOpenNew}
            className="flex items-center gap-2 px-6 py-3.5 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-2xl text-xs font-extrabold uppercase tracking-wider transition-all shadow-lg shadow-seryi-green/25 hover:scale-105"
          >
            <Plus size={16} />
            <span>Crear Nuevo Cupón</span>
          </button>
        </div>
      </div>

      {/* Métricas Rápidas del Programa de Promociones */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-amber-500/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Cupones Activos</span>
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-400">
              <Ticket size={18} />
            </div>
          </div>
          <p className="text-3xl font-black text-amber-400">{stats.activos}</p>
          <p className="text-xs text-seryi-text mt-1">De un total de {coupons.length} promociones</p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-seryi-green/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Total de Canjes</span>
            <div className="w-10 h-10 rounded-2xl bg-seryi-green/10 flex items-center justify-center text-seryi-green">
              <Award size={18} />
            </div>
          </div>
          <p className="text-3xl font-black text-seryi-green">{stats.totalCanjes}</p>
          <p className="text-xs text-seryi-text mt-1">Usos en citas y pedidos verificados</p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-cyan-500/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Valor Canjeado Estimado</span>
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-400">
              <DollarSign size={18} />
            </div>
          </div>
          <p className="text-3xl font-black text-cyan-400">${stats.ahorroGenerado.toLocaleString()}</p>
          <p className="text-xs text-seryi-text mt-1">Incentivo aplicado a ventas exitosas</p>
        </div>
      </div>

      {/* Sección VIP: Programa Digital de Sellos / Lealtad ("Digital Punch Card") */}
      <div className="bg-gradient-to-r from-seryi-bg via-seryi-bg to-seryi-bg-dark border border-amber-500/30 p-6 md:p-8 rounded-3xl shadow-xl">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 text-amber-300 text-[11px] font-extrabold uppercase tracking-wider border border-amber-500/30">
              <Sparkles size={13} />
              <span>Programa de Lealtad Recurrente</span>
            </div>
            <h3 className="text-xl font-extrabold text-seryi-heading">
              Tarjeta Digital VIP de Puntos y Sellos ("Punch Card")
            </h3>
            <p className="text-xs text-seryi-text leading-relaxed">
              Premia a tus clientes por su lealtad constante. Por cada {loyaltyConfig.visitsRequired} visitas registradas en la agenda, el sistema les habilita de forma automática su recompensa VIP.
            </p>
          </div>

          {/* Tarjeta Visual Estilo Gold VIP Card */}
          <div className="w-full lg:w-96 bg-gradient-to-br from-amber-500/25 via-amber-900/30 to-[#0f172a] border border-amber-500/50 p-5 rounded-2xl shadow-2xl relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-amber-300">
                {activeBusiness?.nombre_negocio || "KEYLA CRM"} • VIP CLUB
              </span>
              <Award size={20} className="text-amber-400" />
            </div>
            
            <p className="text-xs font-extrabold text-white mb-3">
              {loyaltyConfig.rewardLabel}
            </p>

            {/* Círculos de sellos (ejemplo para 5 visitas) */}
            <div className="flex items-center gap-2.5 my-3">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-xs transition-all ${
                    idx < 3 
                      ? 'bg-amber-400 text-slate-900 shadow-lg shadow-amber-500/30 scale-105' 
                      : 'bg-slate-800/80 border border-slate-700 text-slate-400'
                  }`}
                >
                  {idx < 3 ? <CheckCircle2 size={18} /> : idx + 1}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-amber-500/20 text-[11px]">
              <span className="text-amber-200/80">Progreso del Cliente: 3/5</span>
              <button
                onClick={handleToggleLoyaltyProgram}
                className={`px-3 py-1 rounded-full font-extrabold text-[10px] uppercase transition-all ${
                  loyaltyConfig.enabled
                    ? 'bg-emerald-500 text-slate-900 shadow-md'
                    : 'bg-slate-700 text-slate-300'
                }`}
              >
                {loyaltyConfig.enabled ? 'ACTIVO ✓' : 'INACTIVO'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Cupones Activos y Pausados */}
      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl shadow-xl overflow-hidden">
        <div className="p-6 border-b border-seryi-bg-light flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-black text-seryi-heading">
              Códigos Promocionales y Cupones ({coupons.length})
            </h3>
            <p className="text-xs text-seryi-text">
              Comparte estos códigos con tus clientes para que los canjeen al agendar su cita o realizar su pedido online.
            </p>
          </div>
          <span className="text-xs font-bold text-seryi-text bg-seryi-bg-dark px-3 py-1.5 rounded-xl border border-seryi-bg-light">
            Portal Online Listo para Canje ✓
          </span>
        </div>

        {coupons.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-seryi-bg-dark border border-seryi-bg-light flex items-center justify-center text-seryi-text mx-auto">
              <Ticket size={28} />
            </div>
            <h4 className="text-base font-bold text-seryi-heading">Aún no has creado cupones</h4>
            <p className="text-xs text-seryi-text max-w-sm mx-auto">
              Haz clic en "Crear Nuevo Cupón" para ofrecer descuentos atractivos en tu portal de reservas.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {coupons.map(cp => {
              const isActive = cp.estado === 'active';
              const progressPct = cp.limite_usos > 0 
                ? Math.min(100, Math.round((cp.usos_actuales / cp.limite_usos) * 100))
                : 0;

              return (
                <div
                  key={cp.id}
                  className={`border rounded-3xl p-6 transition-all duration-200 flex flex-col justify-between relative overflow-hidden group ${
                    isActive
                      ? 'bg-seryi-bg-dark/80 border-seryi-bg-light hover:border-seryi-green/50 shadow-lg'
                      : 'bg-slate-900/40 border-slate-800 opacity-60'
                  }`}
                >
                  <div className="space-y-4">
                    {/* Encabezado Tarjeta de Cupón */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-base font-black tracking-wider text-seryi-heading bg-seryi-bg px-3.5 py-1.5 rounded-xl border border-seryi-bg-light shadow-inner flex items-center gap-2">
                          <Tag size={15} className="text-amber-400" />
                          <span>{cp.codigo}</span>
                        </span>
                      </div>

                      <button
                        onClick={() => handleToggleStatus(cp.id)}
                        className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase transition-all ${
                          isActive
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {isActive ? 'ACTIVO ✓' : 'PAUSADO'}
                      </button>
                    </div>

                    {/* Descuento Destacado */}
                    <div>
                      <p className="text-2xl font-black text-seryi-green flex items-baseline gap-1">
                        {cp.tipo === 'percentage' ? (
                          <>
                            <span>{cp.valor}%</span>
                            <span className="text-xs text-seryi-text font-bold uppercase">OFF</span>
                          </>
                        ) : (
                          <>
                            <span>${cp.valor.toLocaleString()}</span>
                            <span className="text-xs text-seryi-text font-bold uppercase">DESCUENTO FIJO</span>
                          </>
                        )}
                      </p>
                      <p className="text-xs text-seryi-text mt-1 line-clamp-2">
                        {cp.descripcion}
                      </p>
                    </div>

                    {/* Progreso de Usos */}
                    <div className="space-y-1.5 pt-2 border-t border-seryi-bg-light">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-seryi-text font-medium">Usos registrados:</span>
                        <span className="font-extrabold text-seryi-heading">
                          {cp.usos_actuales} / {cp.limite_usos > 900 ? '∞' : cp.limite_usos}
                        </span>
                      </div>
                      <div className="w-full bg-seryi-bg h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-seryi-green to-emerald-400 h-full rounded-full transition-all duration-500"
                          style={{ width: `${progressPct}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Datos Menores */}
                    <div className="flex items-center justify-between text-[11px] text-seryi-text">
                      <span>Mínimo: ${cp.minimo_compra?.toLocaleString() || '0'}</span>
                      <span>Vence: {cp.fecha_fin || 'Sin límite'}</span>
                    </div>
                  </div>

                  {/* Acciones del Cupón */}
                  <div className="flex items-center justify-between gap-2 pt-4 border-t border-seryi-bg-light mt-4">
                    <button
                      onClick={() => handleShareWhatsApp(cp)}
                      className="flex-1 py-2 bg-seryi-green/15 hover:bg-seryi-green/25 text-seryi-green border border-seryi-green/30 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                    >
                      <Share2 size={13} />
                      <span>WhatsApp</span>
                    </button>

                    <button
                      onClick={() => handleOpenEdit(cp)}
                      className="p-2 bg-seryi-bg hover:bg-seryi-bg-light text-seryi-text hover:text-seryi-heading rounded-xl transition-all border border-seryi-bg-light"
                      title="Editar Cupón"
                    >
                      <Edit2 size={14} />
                    </button>

                    <button
                      onClick={() => handleDeleteCoupon(cp.id, cp.codigo)}
                      className="p-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-xl transition-all border border-rose-500/20"
                      title="Eliminar Cupón"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Creación / Edición de Cupón */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-seryi-bg border border-seryi-bg-light w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-seryi-bg-light flex items-center justify-between bg-seryi-bg-dark">
              <div className="flex items-center gap-2">
                <Ticket className="text-amber-400" size={20} />
                <h3 className="font-extrabold text-base text-seryi-heading">
                  {editingCoupon ? 'Editar Código Promocional' : 'Crear Nuevo Cupón VIP'}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-seryi-text hover:text-seryi-heading text-lg font-bold px-2"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveCoupon} className="p-6 space-y-4">
              {/* Código Promo */}
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-seryi-text flex items-center justify-between">
                  <span>Código del Cupón</span>
                  <button
                    type="button"
                    onClick={handleGenerateRandomCode}
                    className="text-[10px] text-seryi-green hover:underline flex items-center gap-1"
                  >
                    <RefreshCw size={11} />
                    <span>Generar Aleatorio</span>
                  </button>
                </label>
                <input
                  type="text"
                  required
                  placeholder="ej. VERANO20"
                  value={formCodigo}
                  onChange={(e) => setFormCodigo(e.target.value.toUpperCase().trim())}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-amber-400 focus:outline-none focus:border-amber-400"
                />
              </div>

              {/* Tipo y Valor del Descuento */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-seryi-text">
                    Tipo Descuento
                  </label>
                  <select
                    value={formTipo}
                    onChange={(e) => setFormTipo(e.target.value)}
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2.5 text-xs text-seryi-heading font-bold focus:outline-none"
                  >
                    <option value="percentage">Porcentaje (%)</option>
                    <option value="fixed">Monto Fijo ($)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-seryi-text">
                    Valor ({formTipo === 'percentage' ? '%' : '$'})
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    placeholder="20"
                    value={formValor}
                    onChange={(e) => setFormValor(e.target.value)}
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-sm font-bold text-seryi-green focus:outline-none focus:border-seryi-green"
                  />
                </div>
              </div>

              {/* Límite y Compra Mínima */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-seryi-text">
                    Límite de Usos
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="100"
                    value={formLimite}
                    onChange={(e) => setFormLimite(e.target.value)}
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-seryi-text">
                    Mínimo de Compra ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formMinimo}
                    onChange={(e) => setFormMinimo(e.target.value)}
                    className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                  />
                </div>
              </div>

              {/* Expiración */}
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-seryi-text">
                  Fecha de Expiración
                </label>
                <input
                  type="date"
                  value={formFechaFin}
                  onChange={(e) => setFormFechaFin(e.target.value)}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                />
              </div>

              {/* Descripción */}
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-seryi-text">
                  Descripción / Detalle Promo
                </label>
                <input
                  type="text"
                  placeholder="ej. 20% de bienvenida en primera reserva"
                  value={formDescripcion}
                  onChange={(e) => setFormDescripcion(e.target.value)}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none"
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-seryi-bg-light">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-seryi-text hover:text-seryi-heading transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-xl text-xs font-extrabold uppercase transition-all shadow-lg"
                >
                  {editingCoupon ? 'Guardar Cambios' : 'Crear Cupón'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
